import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent } from "react";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { Button } from "@/components/ui/button";
import { BrandLogo } from "@/components/brand-logo";

export const Route = createFileRoute("/auth")({
  head: () => ({
    meta: [
      { title: "Acesso restrito · Aleph Comex" },
      { name: "description", content: "Área restrita da equipe Aleph Comex para acompanhamento de contatos comerciais recebidos pelo site." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Acesso restrito · Aleph Comex" },
      { property: "og:description", content: "Área restrita da equipe Aleph Comex." },
    ],
  }),
  component: AuthPage,
});

function AuthPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<"signin" | "signup" | "reset">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) navigate({ to: "/painel" });
    });
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_IN" && session) navigate({ to: "/painel" });
    });
    return () => sub.subscription.unsubscribe();
  }, [navigate]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    if (mode === 'reset') {
      await supabase.auth.resetPasswordForEmail(email, { redirectTo: `${window.location.origin}/reset-password` });
      setBusy(false);
      setMessage('Se houver uma conta com esse e-mail, você receberá um link para definir sua senha.');
      return;
    }
    if (mode === "signup") {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { emailRedirectTo: window.location.origin },
      });
      setBusy(false);
      if (error) return setMessage(error.message);
      if (!data.session) return setMessage("Confira seu e-mail para confirmar o cadastro e depois faça login.");
      return;
    }
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setBusy(false);
    if (error) setMessage("E-mail ou senha incorretos.");
  }

  async function google() {
    setMessage("");
    const result = await lovable.auth.signInWithOAuth("google", {
      redirect_uri: window.location.origin,
    });
    if (result.error) setMessage("Não foi possível entrar com o Google.");
  }

  return (
    <div className="grid min-h-dvh place-items-center bg-surface-soft px-5 py-16">
      <div className="w-full max-w-md bg-background p-8 shadow-sm md:p-10">
        <BrandLogo />
        <h1 className="mt-8 font-display text-2xl font-semibold">Painel de contatos</h1>
        <p className="mt-2 text-sm text-muted-foreground">Acesso restrito à equipe Aleph Comex.</p>
        <form onSubmit={submit} className="mt-8 grid gap-4">
          <label className="grid gap-2 text-sm font-medium">
            E-mail
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 border border-input bg-background px-3 outline-none focus:border-brand" />
          </label>
          {mode !== 'reset' && <label className="grid gap-2 text-sm font-medium">
            Senha
            <input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} className="h-12 border border-input bg-background px-3 outline-none focus:border-brand" />
          </label>}
          <Button type="submit" size="lg" disabled={busy}>
            {busy ? "Aguarde..." : mode === "signin" ? "Entrar" : mode === 'reset' ? 'Enviar link de senha' : "Criar acesso"}
          </Button>
        </form>
        <Button variant="outline" className="mt-3 w-full" onClick={google}>
          Entrar com Google
        </Button>
        <Button variant="link" onClick={() => setMode(mode === "signin" ? "signup" : "signin")} className="mt-6">
          {mode === "signin" ? "Primeiro acesso? Criar conta" : "Já tenho acesso. Entrar"}
        </Button>
        <Button variant="link" className="mt-2" onClick={() => setMode('reset')}>Definir ou recuperar senha</Button>
        {message && <p role="status" className="mt-5 text-sm text-destructive">{message}</p>}
      </div>
    </div>
  );
}
